import { makeAutoObservable } from 'mobx'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { NAME_INBOX } from 'entities/Inbox'
import type { SmartView } from 'entities/SmartViews/model/SmartView'
import { SmartViewModalStore } from 'widgets/SmartViewModal'
import type { ConversationMenuStore } from 'pages/conversations/ui/ConversationMenu'
import { SmartViewDeleteModalStore } from './SmartViewDeleteModalStore'

export class SmartViewsMenuStore {
  private _search = ''
  readonly smartViewModalStore: SmartViewModalStore
  readonly smartViewDeleteModalStore: SmartViewDeleteModalStore
  private _loading = true

  constructor(
    private _conversationMenuStore: ConversationMenuStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)

    this.smartViewModalStore = new SmartViewModalStore(this._entities)
    this.smartViewModalStore.setConfig({
      onAdd: (item) => {
        this._conversationMenuStore.addItemTree({
          inbox: item,
        })
      },
      onEdit: (id) => {
        if (this._entities.inboxesStore.localInboxes.currentSmartViewInboxId === id) {
          this._conversationMenuStore.config?.reloadConversations?.()
        }
      },
    })

    this.smartViewDeleteModalStore = new SmartViewDeleteModalStore()
    this.smartViewDeleteModalStore.setConfig({
      onDelete: async (id) => {
        if (this._entities.inboxesStore.localInboxes.currentSmartViewInboxId === id) {
          this._entities.inboxesStore.handleUpdateTeamInbox(0).then(() => {
            this._entities.inboxesStore.localInboxes.setCurrentInbox('all')
            this._conversationMenuStore.config?.reloadConversations?.()
          })
        }

        this._conversationMenuStore.deleteItemTree(id, 'all', 'smart')
      },
    })
  }

  setSearch = (value: string) => {
    this._search = value
  }

  onOpenModalEdit = (item: SmartView) => {
    this.smartViewModalStore.openModal(item)
  }

  onOpenModalCreate = () => {
    this.smartViewModalStore.openModal()
  }

  onOpenModalDelete = (item: SmartView) => {
    this.smartViewDeleteModalStore.openModal(item)
  }

  dispose = () => {
    this.smartViewModalStore.dispose()
    this._search = ''
  }

  setActiveItem = (id: number) => {
    this._conversationMenuStore.deleteItemTree(NAME_INBOX.Create)
    this.config?.resetConversations?.(false)
    this.config?.setConversationsLoading?.(true)
    this._conversationMenuStore.resetCurrentConversation()
    this._entities.inboxesStore.localInboxes.setCurrentInbox(id)
    this._conversationMenuStore.handleUpdateTeamInbox(0)

    this._entities.eventLogStore.logEvent(
      'Smart View Opened',
      {
        event_id: 'smartview_opened',
        smartViewId: id,
      },
      { groupId: this._entities.organizationStore.id }
    )
  }

  fetchSmartViewsItems = async () => {
    this.setLoading(true)

    await this._entities.smartViewsStore.fetchSmartViews()

    const smartViews = this._entities.smartViewsStore.itemsList

    this._conversationMenuStore.addItemsTree([...smartViews])

    this.setLoading(false)
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  get loading() {
    return this._loading || this._entities.featureFlagsStore.loading
  }

  get search() {
    return this._search
  }

  get items() {
    return this._conversationMenuStore.selectInboxesFreeSort.filter((item) => {
      return !item.data.is_favorite && item.data.type === 'smart_view-inbox'
    })
  }

  get hasItems() {
    return Boolean(this.items.length)
  }

  get itemsTreeSearch() {
    if (this._search.length >= 3) {
      return this.items.filter((item) =>
        item.text.toUpperCase().includes(this._search.toUpperCase())
      )
    }

    return this.items
  }

  get config() {
    return this._conversationMenuStore.config
  }
}
